import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  saveMitigationSuggestion,
  clearMitigationSuggestion,
} from "stores/modules/cap/selfassessmentreducer";

import { useLocation, useNavigate } from "react-router-dom";

import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { fetchAssessmentReportdetailsforReview as fetchAssessmentDetailsService } from "services/modules/cap/standardservice";
import {
  updateMitigations as updateMitigationsService,
  putAssessmentStatus as putAssessmentStatusService,
} from "services/modules/cap/assessmentservice";

import QuestionRenderer from "../../components/shared/QuestionRenderer";

import "../../styles/cap.css";
import { useNotification } from "config/Notification";
import LoadingOverlay from "components/LoadingOverlay.jsx";

const AssignMitigationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlparams = location.state;

  const dispatch = useDispatch();
  const mitigationStore = useSelector(
    (state) =>
      state.capSelfAssessment.assessments[urlparams.assessmentid]
        ?.mitigationSuggestions || {},
  );

  const { openNotification } = useNotification();

  const [assessmentsList, setAssessmentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [mitigationControls, setMitigationControls] = useState({});
  const [submit, setSubmit] = useState(false);

  // ── Details dialog state ──────────────────────────────────────────────────
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSubcontrol, setSelectedSubcontrol] = useState(null);

  const openDetailDialog = (subcontrol) => {
    setSelectedSubcontrol(subcontrol);
    setDetailDialog(true);
  };

  const closeDetailDialog = () => {
    setDetailDialog(false);
    setSelectedSubcontrol(null);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (event, submissionID) => {
    const { value } = event.target;
    dispatch(
      saveMitigationSuggestion({
        assessmentId: urlparams.assessmentid,
        submissionId: submissionID,
        value,
      }),
    );
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      mitigationNext();
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (rowId) => {
    setExpandedRow((prevRow) => (prevRow === rowId ? null : rowId));
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchAssessmentData = async () => {
    setLoading(true);
    try {
      let params = { assessment_id: urlparams.assessmentid };
      const response = await fetchAssessmentDetailsService({ params });
      const controls = response?.data?.controls || response?.data || [];

      if (Array.isArray(controls) && controls.length > 0) {
        setAssessmentsList(controls);
        let mititems = {};
        controls.forEach((e) => {
          e.subcontrols?.forEach((t) => {
            if (t.mitigation) {
              mititems[t.submissionID] = t.mitigation;
            }
          });
        });
        setMitigationControls(mititems);
      } else {
        setAssessmentsList([]);
      }
    } catch (error) {
      openNotification("Error fetching assessment", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pageValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentsList, page, rowsPerPage]);

  // ── Mitigation save / submit ──────────────────────────────────────────────
  const saveMitigationPlan = async (subcontrols) => {
    try {
      let updatedItemList = [];
      let lineitem;

      subcontrols.forEach((e) => {
        let mitigation = mitigationStore[e.submissionID];
        if (mitigation && mitigation.trim() !== "") {
          updatedItemList.push({ submissionId: e.submissionID, mitigation });
        } else {
          if (!mitigationControls[e.submissionID]) {
            lineitem = e;            
          }
        }
      });

      if (updatedItemList.length > 0) {
        let assessment_id = urlparams.assessmentid;
        if (assessment_id) {
          let response = await updateMitigationsService({
            assessment_id,
            data: updatedItemList,
          });
          if (response.status === 201) {
            openNotification("Submitted successfully.", "success");
            dispatch(clearMitigationSuggestion(urlparams.assessmentid));
            await fetchAssessmentData();
          } else {
            openNotification(
              "Failed to save the Mitigation Suggestion",
              "error",
            );
          }
        }
      } else {
        openNotification(
          "Please enter mitigation suggestion for ref #" + lineitem.subcontrol,
          "error",
        );
      }
    } catch (error) {}
  };

  const mitigationSubmit = async () => {
    let isComplete = true;
    assessmentsList.forEach((e) => {
      e.subcontrols.forEach((t) => {
        const value =
          mitigationStore[t.submissionID] ?? mitigationControls[t.submissionID];
        if (!value || value.trim() === "") {
          isComplete = false;
          openNotification(
            "Please enter mitigation suggestion for ref #" + t.subcontrol,
            "error",
          );
        }
      });
    });

    if (isComplete) {
      const currentStatus = urlparams.status;
      if (currentStatus !== "Approved") {
        try {
          await putAssessmentStatusService({
            assessment_id: urlparams.assessmentid,
            status: "Mitigation Initiated",
          });
        } catch (error) {
          console.warn("Status update skipped:", error);
        }
      }

      try {
        let updatedItemList = [];
        assessmentsList.forEach((e) => {
          e.subcontrols.forEach((t) => {
            const mitigation = mitigationStore[t.submissionID];
            if (mitigation && mitigation.trim() !== "") {
              updatedItemList.push({
                submissionId: t.submissionID,
                mitigation,
              });
            }
          });
        });

        const response = await updateMitigationsService({
          assessment_id: urlparams.assessmentid,
          data: updatedItemList,
        });
        if (response.status === 200 || response.status === 201) {
          dispatch(clearMitigationSuggestion(urlparams.assessmentid));
          navigate("/cap/mitigationsuccess", {
            state: {
              assessmentid: urlparams.assessmentid,
              standard: urlparams.standard,
              status: "Mitigation Initiated",
            },
          });
        } else {
          openNotification("Failed to submit mitigation", "error");
        }
      } catch (error) {
        openNotification("Error submitting mitigation", "error");
      }
    }
  };

  const mitigationNext = async () => {
    try {
      await validateMitigationData();
    } catch (error) {}
  };

  const validateMitigationData = async () => {
    const length = Math.ceil(rowsPerPage * (page + 1));
    let isProceed = true;
    const maxLength = Math.min(length, assessmentsList.length);
    for (let i = 0; i < maxLength; i++) {
      for (let j = 0; j < assessmentsList[i].subcontrols.length; j++) {
        const submissionID = assessmentsList[i].subcontrols[j].submissionID;

        const value =
          mitigationStore[submissionID] ?? mitigationControls[submissionID];

        if (!value || value.trim() === "") {
          openNotification(
            "Please enter mitigation suggestion for ref #" +
              assessmentsList[i].subcontrols[j].subcontrol,
            "error",
          );
          isProceed = false;
          return false;
        }
      }
    }
    if (isProceed) {
      setPage(page + 1);
      pageValidate();
    } else {
      setPage(page);
    }
  };

  const pageValidate = () => {
    const tableLength = rowsPerPage * (page + 1);
    const remainingLength = assessmentsList.length - 1;
    setSubmit(remainingLength <= tableLength);
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="row py-1">
        <p>
          <img
            className="px-1 icons-style"
            src="../assets/icons/003-clipboard.svg"
            alt="View"
          />
          <b className="px-1">Assign Mitigation </b>
          <img
            className="px-1 icons-style"
            src="../assets/icons/right-layer.svg"
            alt="View"
          />
          {urlparams.standard === "CIS" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-cis.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "ISO-27001" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-iso-27001.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "NIST" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-nist.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "COBIT" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-cobit.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "TISAX" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-tisax.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "NIS 2.0" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-nis2.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "Ransomware" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-ransomware.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "Data Protection" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-data-protection.png"
              alt="View"
              draggable="false"
            />
          )}
          {urlparams.standard === "Supplier Risk" && (
            <img
              className="px-sm standard-icon-size"
              src="../assets/icons/standard-supplier-risk.png"
              alt="View"
              draggable="false"
            />
          )}
          <span> {urlparams.standard} </span> -{" "}
          <span>{urlparams.assessmentid}</span>
        </p>
      </div>

      {loading && <LoadingOverlay />}

      <div>
        {assessmentsList.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>Control name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? assessmentsList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : assessmentsList
                ).map((item) => (
                  <React.Fragment key={item.controlID}>
                    {/* ── Outer control row ── */}
                    <TableRow>
                      <TableCell>
                        <IconButton
                          className="expand-row"
                          size="small"
                          onClick={() => handleRowClick(item.controlID)}
                        >
                          {expandedRow === item.controlID ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {item.controlID + " : " + item.controlName}
                      </TableCell>
                      <TableCell>{item.assessment_id}</TableCell>
                    </TableRow>

                    {/* ── Expanded subcontrols row ── */}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0 }}>
                        <Collapse
                          in={expandedRow === item.controlID}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: "7%" }}>Ref</TableCell>
                                <TableCell sx={{ width: "23%" }}>
                                  Control Question
                                </TableCell>
                                <TableCell sx={{ width: "18%" }}>
                                  Answer Given
                                </TableCell>
                                <TableCell sx={{ width: "25%" }}>
                                  Mitigation Suggestion
                                </TableCell>
                                <TableCell align="center" sx={{ width: "12%" }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.subcontrols.map((subcontrol) => {
                                const qType =
                                  subcontrol.question_type || "five_option";
                                return (
                                  <TableRow key={subcontrol.subcontrolID}>
                                    {/* Ref */}
                                    <TableCell
                                      sx={{
                                        width: "7%",
                                        whiteSpace: "nowrap",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {subcontrol.subcontrol}
                                    </TableCell>

                                    {/* Control Question */}
                                    <TableCell
                                      sx={{
                                        width: "23%",
                                        verticalAlign: "top",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <div className="subcontrols">
                                        {subcontrol.subcontrolName}
                                      </div>
                                    </TableCell>

                                    {/* Answer Given — QuestionRenderer read-only */}
                                    <TableCell
                                      sx={{
                                        width: "18%",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      <QuestionRenderer
                                        question_type={qType}
                                        answer_config={
                                          subcontrol.answer_config || null
                                        }
                                        value={
                                          subcontrol.answer_value ||
                                          subcontrol.severity
                                        }
                                        readOnly={true}
                                      />
                                    </TableCell>

                                    {/* Mitigation Suggestion textarea */}
                                    <TableCell
                                      sx={{
                                        width: "25%",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      <textarea
                                        key={subcontrol.submissionID}
                                        id={subcontrol.submissionID}
                                        className="mitigationControls"
                                        value={
                                          mitigationStore[
                                            subcontrol.submissionID
                                          ] ??
                                          mitigationControls[
                                            subcontrol.submissionID
                                          ] ??
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            e,
                                            subcontrol.submissionID,
                                          )
                                        }
                                        placeholder="Enter mitigation suggestion..."
                                        style={{
                                          width: "100%",
                                          minHeight: "80px",
                                          resize: "vertical",
                                        }}
                                      />
                                    </TableCell>

                                    {/* Actions — View Details button */}
                                    <TableCell
                                      align="center"
                                      sx={{
                                        width: "12%",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<InfoOutlinedIcon />}
                                        onClick={() =>
                                          openDetailDialog(subcontrol)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                          whiteSpace: "nowrap",
                                          borderColor: "#1976d2",
                                          color: "#1976d2",
                                          "&:hover": {
                                            backgroundColor: "#e3f2fd",
                                          },
                                        }}
                                      >
                                        View Details
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Save row */}
                              <TableRow>
                                <TableCell colSpan={5} className="rating-value">
                                  <button
                                    className="approve-button"
                                    style={{ width: "80px" }}
                                    type="button"
                                    onClick={() =>
                                      saveMitigationPlan(item.subcontrols)
                                    }
                                  >
                                    Save
                                  </button>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 7, 10, 15, 25]}
              component="div"
              count={assessmentsList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* ── Action buttons ── */}
        {assessmentsList.length > 0 && (
          <div className="reject-alignment">
            {submit ? (
              <button
                className="approve-button reject-btn"
                onClick={() => mitigationSubmit()}
              >
                Submit
              </button>
            ) : (
              <button
                className="approve-button reject-btn"
                onClick={() => mitigationNext()}
              >
                Next
              </button>
            )}
            <button
              className="go-back-button reject-btn"
              onClick={() => window.history.back()}
            >
              <ArrowLeftIcon /> Go back
            </button>
          </div>
        )}
      </div>

      {/* ── View Details dialog ── */}
      {detailDialog && selectedSubcontrol && (
        <Dialog
          open={detailDialog}
          onClose={closeDetailDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "10px" } }}
        >
          <DialogTitle
            sx={{ m: 0, p: 2, backgroundColor: "#1a2b5e", color: "#fff" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              <span style={{ fontSize: "15px", fontWeight: 600 }}>
                {selectedSubcontrol.subcontrol} — Question Details
              </span>
            </div>
            <IconButton
              aria-label="close"
              onClick={closeDetailDialog}
              sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            {/* Control Question */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}
              >
                Control Question
              </div>
              <div
                style={{ fontSize: "14px", fontWeight: 600, color: "#1a2b5e" }}
              >
                {selectedSubcontrol.subcontrolName}
              </div>
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Answer Given */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                }}
              >
                Answer Given
              </div>
              <QuestionRenderer
                question_type={
                  selectedSubcontrol.question_type || "five_option"
                }
                answer_config={selectedSubcontrol.answer_config || null}
                value={
                  selectedSubcontrol.answer_value || selectedSubcontrol.severity
                }
                readOnly={true}
              />
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Rationale */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                }}
              >
                Rationale for Rating
              </div>
              <div
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  fontSize: "13px",
                  lineHeight: "1.6",
                  color: selectedSubcontrol.rational ? "#333" : "#aaa",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {selectedSubcontrol.rational || "No rationale provided"}
              </div>
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Mitigation suggestion — live from Redux store */}
            <div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                }}
              >
                Mitigation Suggestion
              </div>
              <div
                style={{
                  background: "#f0f7ff",
                  border: "1px solid #b3d4f5",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  fontSize: "13px",
                  lineHeight: "1.6",
                  color: mitigationStore[selectedSubcontrol.submissionID]
                    ? "#1a2b5e"
                    : "#aaa",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {mitigationStore[selectedSubcontrol.submissionID] ??
                  mitigationControls[selectedSubcontrol.submissionID] ??
                  "No mitigation entered yet"}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AssignMitigationPage;
